// Meta (Facebook) Pixel helpers.
//
// The Pixel base code (init + initial PageView) is installed in
// public/index.html. This module fires SPA route PageViews and the
// conversion events. For CompleteRegistration / StartTrial / Purchase the
// backend is authoritative: it sends the Conversions API event and returns
// the event_id + custom_data, and we fire the matching browser Pixel event
// with the SAME eventID so Meta deduplicates the pair. Plan/price values come
// from the server (billing PLANS) — never hardcoded here. Email is never sent
// from the browser; the server hashes the authenticated user's email.

import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? m[2] : undefined;
}

export function metaPageView() {
  try {
    if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
  } catch { /* analytics must never break UX */ }
}

/**
 * Fire a server-backed conversion (CompleteRegistration | StartTrial | Purchase).
 * Requires an authenticated session (email is read server-side).
 * @param {string} event
 * @param {{planId?: string, cadence?: string, sourceUrl?: string}} [opts]
 */
export async function metaTrack(event, opts = {}) {
  try {
    const res = await apiFetch(`${API_URL}/api/meta/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        plan_id: opts.planId,
        cadence: opts.cadence || 'monthly',
        event_source_url: opts.sourceUrl || window.location.href,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
      }),
    });
    if (!res.ok) return;
    const d = await res.json();
    if (!d.event_id || typeof window.fbq !== 'function') return;
    const params = {};
    ['value', 'currency', 'predicted_ltv', 'content_name', 'content_ids', 'content_type']
      .forEach((k) => { if (d[k] !== undefined && d[k] !== null) params[k] = d[k]; });
    window.fbq('track', event, params, { eventID: d.event_id });
  } catch { /* never block UX on analytics */ }
}

/** Browser-only Lead event for contact / demo / booking form submits. */
export function metaLead(params = {}) {
  try {
    if (typeof window.fbq === 'function') window.fbq('track', 'Lead', params);
  } catch { /* ignore */ }
}

/**
 * InitiateCheckout — fired when a visitor selects/clicks a plan, so Meta can
 * optimize ad delivery earlier in the funnel. Value is the plan's monthly
 * price; currency USD. Fires from BOTH the browser Pixel and the server
 * Conversions API with the SAME eventID so Meta deduplicates the pair. The
 * server endpoint is public (anonymous visitors on the pricing page) — it
 * builds price/content from billing PLANS, never trusting a client value.
 */
export function metaInitiateCheckout({ planId, value, planName, cadence } = {}) {
  let eventId;
  try {
    eventId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID().replace(/-/g, '')
      : `ic_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  } catch { eventId = `ic_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; }

  // Server-side CAPI (fire-and-forget) with the shared eventID.
  try {
    fetch(`${API_URL}/api/meta/track-initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_id: eventId,
        plan_id: planId,
        cadence: cadence || 'monthly',
        event_source_url: window.location.href,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never block UX */ }

  // Browser Pixel with the SAME eventID.
  try {
    if (typeof window.fbq !== 'function') return;
    const params = { currency: 'USD', content_type: 'product' };
    if (value != null && value !== '') params.value = Number(value);
    if (planName) params.content_name = planName;
    if (planId) params.content_ids = [planId];
    window.fbq('track', 'InitiateCheckout', params, { eventID: eventId });
  } catch { /* ignore */ }
}
